import { call, put, takeLatest } from "redux-saga/effects";
import { axiosRestInstance } from "../../../common/axios";
import { AxiosResponse } from "axios";
import { reportsSliceAction } from "../../slices/reports";
import {
  getPartnersReportsSagaActionPayloadType,
  getReportFormatsListSagaAction,
  reportsSagaActionTypes,
} from "./saga-actions";
import { ExportReportFormat } from "../../slices/reports/Types";

const getPartnersReportAPI = async (
  payload: getPartnersReportsSagaActionPayloadType
) => {
  const response = await axiosRestInstance.post<AxiosResponse<Blob>>(
    "/reports/export-all-contracts",
    payload,
    {
      responseType: "blob",
    }
  );
  return response;
};

type getReportFormatsListAPIResponseType = {
  success: boolean;
  message: string;
  data: {
    saved_export_format: string;
    selected_options: ExportReportFormat;
  }[];
};
const getReportFormatsListAPI = async () => {
  const response = await axiosRestInstance
    .get<AxiosResponse<getReportFormatsListAPIResponseType>>(
      "/reports/get-report"
    )
    .then((res) => res.data);
  return response;
};

type saveReportFormatAPIResponseType = {
  success: boolean;
  message: string;
};
const saveReportFormatAPI = async (payload: {
  saved_export_format: string;
  selected_options: ExportReportFormat;
}) => {
  const response = await axiosRestInstance
    .post<AxiosResponse<saveReportFormatAPIResponseType>>(
      "/reports/save-report",
      payload
    )
    .then((res) => res.data);
  return response;
};

type deleteReportFormatAPIResponseType = {
  success: boolean;
  message: string;
};
const deleteReportFormatAPI = async (id: number) => {
  const response = await axiosRestInstance
    .delete<AxiosResponse<deleteReportFormatAPIResponseType>>(`/reports/${id}`)
    .then((res) => res.data);
  return response;
};

// API above - Sagas Below

function getFileNameFromContentDisposition(
  contentDisposition: string | undefined
): string {
  if (!contentDisposition) return "report.xlsx";
  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
  return filenameMatch?.[1] ?? "report.xlsx";
}

function* getPartnersReportSaga(action: {
  type: string;
  payload: getPartnersReportsSagaActionPayloadType;
}) {
  try {
    yield put(reportsSliceAction.setPartnersReportLoading(true));
    const response: AxiosResponse<Blob> = (yield call(
      getPartnersReportAPI,
      action.payload
    )) as AxiosResponse<Blob>;

    const contentDisposition = response.headers["content-disposition"];
    const filename = getFileNameFromContentDisposition(contentDisposition);

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error: any) {
    console.error("Download failed: ", error);
  } finally {
    yield put(reportsSliceAction.setPartnersReportLoading(false));
  }
}

function* getReportFormatsListSaga(action: { type: string }) {
  try {
    yield put(reportsSliceAction.setReportFormatsListLoading(true));
    let response: getReportFormatsListAPIResponseType = yield call(
      getReportFormatsListAPI
    );
    yield put(reportsSliceAction.setReportFormatsList(response.data || []));
  } catch (e) {
    yield put(reportsSliceAction.setReportFormatsList([]));
    console.log(e);
  } finally {
    yield put(reportsSliceAction.setReportFormatsListLoading(false));
  }
}

function* saveReportFormatSaga(action: {
  type: string;
  data: {
    payload: {
      saved_export_format: string;
      selected_options: ExportReportFormat;
    };
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
}) {
  try {
    yield put(reportsSliceAction.setSaveReportFormatLoading(true));
    let response: getReportFormatsListAPIResponseType = yield call(
      saveReportFormatAPI,
      action.data.payload
    );
    yield put(getReportFormatsListSagaAction());
    action.data.setModalOpen(false);
  } catch (e) {
    console.log(e);
  } finally {
    yield put(reportsSliceAction.setSaveReportFormatLoading(false));
  }
}

function* deleteReportFormatSaga(action: {
  type: string;
  payload: {
    id: number;
    setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  };
}) {
  try {
    yield put(reportsSliceAction.setDeleteReportFormatLoading(true));
    let response: deleteReportFormatAPIResponseType = yield call(
      deleteReportFormatAPI,
      action.payload.id
    );
    yield put(getReportFormatsListSagaAction());
    action.payload.setModalOpen(false);
  } catch (e) {
    console.log(e);
  } finally {
    yield put(reportsSliceAction.setDeleteReportFormatLoading(false));
  }
}

function* reportsSaga() {
  yield takeLatest(
    reportsSagaActionTypes.callGetPartnersReportsSaga,
    getPartnersReportSaga
  );
  yield takeLatest(
    reportsSagaActionTypes.callGetReportFormatsListSaga,
    getReportFormatsListSaga
  );
  yield takeLatest(
    reportsSagaActionTypes.callSaveReportFormatSaga,
    saveReportFormatSaga
  );
  yield takeLatest(
    reportsSagaActionTypes.callDeleteReportFormatSaga,
    deleteReportFormatSaga
  );
}

export default reportsSaga;
